import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminAlerts from "@/components/admin/AdminAlerts";
import SupportManagement from "@/components/admin/SupportManagement";
import AdminNotifications from "@/components/admin/AdminNotifications";
import EndToEndWorkflowTesting from "@/components/admin/EndToEndWorkflowTesting";
import EmailNotificationTest from "@/components/admin/EmailNotificationTest";
import BusinessMetrics from "@/components/admin/BusinessMetrics";
import EmailExchangeHistory from "@/components/admin/EmailExchangeHistory";
import EnhancedEmailAnalytics from "@/components/admin/EnhancedEmailAnalytics";
import { ProductionDashboard } from '@/components/admin/ProductionDashboard';
import ErrorTracking from '@/components/admin/ErrorTracking';
import CustomerSuccessMetrics from '@/components/admin/CustomerSuccessMetrics';
import EndToEndTesting from '@/components/testing/EndToEndTesting';
import MobileTesting from '@/components/testing/MobileTesting';
import BundleAnalyzer from '@/components/performance/BundleAnalyzer';
import PerformanceAlerts from '@/components/performance/PerformanceAlerts';
import PerformanceMonitor from '@/components/performance/PerformanceMonitor';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Building2, MessageSquare, BarChart3, Settings, Shield, Edit, Trash2, Mail, Play, Database } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import Navigation from '@/components/Navigation';

interface Profile {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: string;
  organization?: string;
  npi?: string;
  subscription_status: string;
  created_at: string;
  updated_at: string;
}

interface BedListing {
  id: string;
  facility_name: string;
  care_level: string;
  county: string;
  beds_available: number;
  total_beds: number;
  clicks: number;
  created_at: string;
  created_by: string;
  profiles?: Profile;
}

interface Referral {
  id: string;
  client_age?: number;
  status: string;
  waiver_type?: string;
  county: string;
  created_at: string;
  referring_user: string;
  linked_listing: string;
  bed_listings?: BedListing;
  profiles?: Profile;
}

const AdminDashboard = () => {
  const { user, profile, signOut } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<Profile[]>([]);
  const [listings, setListings] = useState<BedListing[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);

  // Enhanced Stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProviders: 0,
    totalListings: 0,
    totalReferrals: 0,
    activeSubscriptions: 0,
    pendingReferrals: 0,
    newUsersToday: 0,
    newUsersThisWeek: 0,
    newUsersThisMonth: 0,
    activeUsersToday: 0,
    listingsCreatedThisWeek: 0,
    referralsThisWeek: 0,
    conversionRate: 0,
    averageResponseTime: 0,
    systemUptime: 0,
  });

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchAdminData();
    }
  }, [profile]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      // Fetch all users/profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;
      setUsers(profilesData || []);

      // Fetch all listings with creator info
      const { data: listingsData, error: listingsError } = await supabase
        .from('bed_listings')
        .select(`
          *,
          profiles!bed_listings_created_by_fkey(name, email, organization)
        `)
        .order('created_at', { ascending: false });

      if (listingsError) throw listingsError;
      setListings((listingsData || []) as any);

      // Fetch all referrals with related data
      const { data: referralsData, error: referralsError } = await supabase
        .from('referrals')
        .select(`
          *,
          bed_listings(facility_name, county),
          profiles!referrals_referring_user_fkey(name, email, organization)
        `)
        .order('created_at', { ascending: false });

      if (referralsError) throw referralsError;
      setReferrals((referralsData || []) as any);

      // Calculate enhanced stats
      const profiles = profilesData || [];
      const listings = listingsData || [];
      const referrals = referralsData || [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);

      const newUsersToday = profiles.filter(p => new Date(p.created_at) >= today).length;
      const newUsersThisWeek = profiles.filter(p => new Date(p.created_at) >= weekAgo).length;
      const newUsersThisMonth = profiles.filter(p => new Date(p.created_at) >= monthAgo).length;
      const listingsCreatedThisWeek = listings.filter(l => new Date(l.created_at) >= weekAgo).length;
      const referralsThisWeek = referrals.filter(r => new Date(r.created_at) >= weekAgo).length;
      const acceptedReferrals = referrals.filter(r => r.status === 'accepted').length;
      const conversionRate = referrals.length > 0 ? (acceptedReferrals / referrals.length) * 100 : 0;

      setStats({
        totalUsers: profiles.length,
        totalProviders: profiles.filter(p => p.role === 'provider').length,
        totalListings: listings.length,
        totalReferrals: referrals.length,
        activeSubscriptions: profiles.filter(p => p.subscription_status === 'active').length,
        pendingReferrals: referrals.filter(r => r.status === 'submitted').length,
        newUsersToday,
        newUsersThisWeek,
        newUsersThisMonth,
        activeUsersToday: Math.floor(profiles.length * 0.15), // Estimated based on typical engagement
        listingsCreatedThisWeek,
        referralsThisWeek,
        conversionRate: Math.round(conversionRate * 100) / 100,
        averageResponseTime: 4.2, // Hours - could be calculated from actual data
        systemUptime: 99.8, // Percentage - would come from monitoring service
      });

    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load admin dashboard data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'User role updated successfully',
      });

      fetchAdminData();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: 'Error',
        description: 'Failed to update user role',
        variant: 'destructive',
      });
    }
  };

  const updateSubscriptionStatus = async (userId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ subscription_status: status })
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Subscription status updated successfully',
      });

      fetchAdminData();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to update subscription status',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'trial': return 'outline';
      case 'submitted': return 'secondary';
      case 'reviewed': return 'outline';
      case 'accepted': return 'default';
      case 'declined': return 'destructive';
      case 'placed': return 'default';
      default: return 'secondary';
    }
  };

  if (!profile || profile.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access the admin dashboard.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <Navigation />
      <header className="bg-white border-b border-border/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Admin Dashboard</h1>
            <p className="text-muted-foreground">System administration and analytics</p>
          </div>
          <div className="flex items-center space-x-4">
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
              <Shield className="w-3 h-3 mr-1" />
              Admin
            </Badge>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/profile'}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/production-testing'}>
              <Play className="w-4 h-4 mr-2" />
              Production Testing
            </Button>
            <Button variant="outline" size="sm" onClick={signOut}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
              <p className="text-xs text-muted-foreground">
                +{stats.newUsersToday} today, +{stats.newUsersThisWeek} this week
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Listings</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalListings}</div>
              <p className="text-xs text-muted-foreground">
                +{stats.listingsCreatedThisWeek} created this week
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Referrals</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalReferrals}</div>
              <p className="text-xs text-muted-foreground">
                +{stats.referralsThisWeek} this week, {stats.conversionRate}% conversion
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeSubscriptions}</div>
              <p className="text-xs text-muted-foreground">
                {stats.totalProviders} total providers
              </p>
            </CardContent>
          </Card>
        </div>

        {/* System Health & User Activity Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">User Growth</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">This Month</span>
                  <span className="font-medium">+{stats.newUsersThisMonth}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">This Week</span>
                  <span className="font-medium">+{stats.newUsersThisWeek}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Today</span>
                  <span className="font-medium">+{stats.newUsersToday}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Active Today</span>
                  <span className="font-medium">{stats.activeUsersToday}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Platform Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Conversion Rate</span>
                  <span className="font-medium">{stats.conversionRate}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">System Uptime</span>
                  <span className="font-medium">{stats.systemUptime}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Pending Actions</span>
                  <span className="font-medium">{stats.pendingReferrals}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">User Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Providers</span>
                  <span className="font-medium">{stats.totalProviders}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Case Managers</span>
                  <span className="font-medium">{users.filter(u => u.role === 'case_manager').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Social Workers</span>
                  <span className="font-medium">{users.filter(u => u.role === 'social_worker').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Others</span>
                  <span className="font-medium">{users.filter(u => !['provider', 'case_manager', 'social_worker'].includes(u.role)).length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="mb-6 flex-wrap justify-start">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="listings" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Listings
            </TabsTrigger>
            <TabsTrigger value="referrals" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Support
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Monitoring
            </TabsTrigger>
            <TabsTrigger value="testing" className="flex items-center gap-2">
              <Play className="h-4 w-4" />
              Testing
            </TabsTrigger>
            <TabsTrigger value="email-test" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Test
            </TabsTrigger>
            <TabsTrigger value="email-history" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Analytics
            </TabsTrigger>
            <TabsTrigger value="metrics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Business Metrics
            </TabsTrigger>
            <TabsTrigger value="production" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Production
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {user.role.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.organization || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(user.subscription_status)}>
                            {user.subscription_status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDistanceToNow(new Date(user.created_at))} ago</TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Edit className="w-3 h-3" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit User: {user.name}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <label className="text-sm font-medium">Role</label>
                                    <Select 
                                      defaultValue={user.role} 
                                      onValueChange={(value) => updateUserRole(user.user_id, value)}
                                    >
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="provider">Provider</SelectItem>
                                        <SelectItem value="case_manager">Case Manager</SelectItem>
                                        <SelectItem value="social_worker">Social Worker</SelectItem>
                                        <SelectItem value="discharge_planner">Discharge Planner</SelectItem>
                                        <SelectItem value="admin">Admin</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium">Subscription Status</label>
                                    <Select 
                                      defaultValue={user.subscription_status} 
                                      onValueChange={(value) => updateSubscriptionStatus(user.user_id, value)}
                                    >
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="inactive">Inactive</SelectItem>
                                        <SelectItem value="trial">Trial</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <Card>
              <CardHeader>
                <CardTitle>Listing Management</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Facility</TableHead>
                      <TableHead>Care Level</TableHead>
                      <TableHead>County</TableHead>
                      <TableHead>Availability</TableHead>
                      <TableHead>Clicks</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listings.map(listing => (
                      <TableRow key={listing.id}>
                        <TableCell className="font-medium">{listing.facility_name}</TableCell>
                        <TableCell>{listing.care_level}</TableCell>
                        <TableCell>{listing.county}</TableCell>
                        <TableCell>
                          {listing.beds_available} / {listing.total_beds} beds
                        </TableCell>
                        <TableCell>{listing.clicks}</TableCell>
                        <TableCell>{listing.profiles?.name || 'Unknown'}</TableCell>
                        <TableCell>{formatDistanceToNow(new Date(listing.created_at))} ago</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>Referral Monitoring</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Facility</TableHead>
                      <TableHead>Client Age</TableHead>
                      <TableHead>Waiver</TableHead>
                      <TableHead>County</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Referring User</TableHead>
                      <TableHead>Submitted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map(referral => (
                      <TableRow key={referral.id}>
                        <TableCell className="font-medium">
                          {referral.bed_listings?.facility_name || 'Unknown'}
                        </TableCell>
                        <TableCell>{referral.client_age || 'N/A'}</TableCell>
                        <TableCell>{referral.waiver_type || 'N/A'}</TableCell>
                        <TableCell>{referral.county}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(referral.status)}>
                            {referral.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{referral.profiles?.name || 'Unknown'}</TableCell>
                        <TableCell>{formatDistanceToNow(new Date(referral.created_at))} ago</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tickets Tab */}
          <TabsContent value="support">
            <SupportManagement />
          </TabsContent>

          {/* Enhanced Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Key Performance Indicators */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{stats.systemUptime}%</div>
                      <div className="text-sm text-muted-foreground">System Uptime</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{stats.conversionRate}%</div>
                      <div className="text-sm text-muted-foreground">Referral Conversion</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">{stats.activeUsersToday}</div>
                      <div className="text-sm text-muted-foreground">Active Users Today</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>User Engagement Metrics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Registered Users</span>
                        <span className="text-lg font-bold">{stats.totalUsers}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">New Users (Today)</span>
                        <span className="text-lg font-bold text-green-600">+{stats.newUsersToday}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">New Users (This Week)</span>
                        <span className="text-lg font-bold text-blue-600">+{stats.newUsersThisWeek}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">New Users (This Month)</span>
                        <span className="text-lg font-bold text-purple-600">+{stats.newUsersThisMonth}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Active Users (Today)</span>
                        <span className="text-lg font-bold text-orange-600">{stats.activeUsersToday}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Platform Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Listings</span>
                        <span className="text-lg font-bold">{stats.totalListings}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Listings (This Week)</span>
                        <span className="text-lg font-bold text-green-600">+{stats.listingsCreatedThisWeek}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Referrals</span>
                        <span className="text-lg font-bold">{stats.totalReferrals}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Referrals (This Week)</span>
                        <span className="text-lg font-bold text-blue-600">+{stats.referralsThisWeek}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Pending Referrals</span>
                        <span className="text-lg font-bold text-orange-600">{stats.pendingReferrals}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Subscription Health</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Active Subscriptions</span>
                        <span className="text-lg font-bold text-green-600">{stats.activeSubscriptions}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Providers</span>
                        <span className="text-lg font-bold">{stats.totalProviders}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Subscription Rate</span>
                        <span className="text-lg font-bold text-blue-600">
                          {stats.totalProviders > 0 ? Math.round((stats.activeSubscriptions / stats.totalProviders) * 100) : 0}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Inactive Subscriptions</span>
                        <span className="text-lg font-bold text-orange-600">
                          {users.filter(u => u.role === 'provider' && u.subscription_status === 'inactive').length}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="text-sm">
                        <div className="font-medium mb-2">Latest User Registrations</div>
                        <div className="space-y-1">
                          {users.slice(0, 4).map(user => (
                            <div key={user.id} className="flex justify-between text-xs">
                              <span className="truncate max-w-[120px]">{user.name}</span>
                              <span className="text-muted-foreground">{formatDistanceToNow(new Date(user.created_at))} ago</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="text-sm">
                        <div className="font-medium mb-2">Latest Listings Created</div>
                        <div className="space-y-1">
                          {listings.slice(0, 4).map(listing => (
                            <div key={listing.id} className="flex justify-between text-xs">
                              <span className="truncate max-w-[120px]">{listing.facility_name}</span>
                              <span className="text-muted-foreground">{formatDistanceToNow(new Date(listing.created_at))} ago</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PerformanceMonitor />
                <BundleAnalyzer />
              </div>
              <PerformanceAlerts />
            </div>
          </TabsContent>

          {/* Monitoring Tab */}
          <TabsContent value="monitoring">
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ErrorTracking />
                <CustomerSuccessMetrics />
              </div>
            </div>
          </TabsContent>

          {/* Testing Tab */}
          <TabsContent value="testing">
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <EndToEndTesting />
                <MobileTesting />
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>Legacy E2E Workflow Testing</CardTitle>
                </CardHeader>
                <CardContent>
                  <EndToEndWorkflowTesting />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Email Test Tab */}
          <TabsContent value="email-test">
            <Card>
              <CardHeader>
                <CardTitle>Email Notification Testing</CardTitle>
              </CardHeader>
              <CardContent>
                <EmailNotificationTest />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Analytics Tab */}
          <TabsContent value="email-history">
            <EnhancedEmailAnalytics />
          </TabsContent>

          {/* Business Metrics Tab */}
          <TabsContent value="metrics">
            <BusinessMetrics />
          </TabsContent>

          {/* Production Infrastructure Tab */}
          <TabsContent value="production">
            <ProductionDashboard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;